class OosVariantDrawer extends HTMLElement {
  connectedCallback() {
    if (this._initialized) return;

    if (window.Drawer) {
      this._init();
    } else {
      window.addEventListener('theme:drawer:ready', () => this._init(), { once: true });
    }
  }

  disconnectedCallback() {
    document.removeEventListener('click', this._clickListener);
    document.removeEventListener('click', this._cartListener);
    document.removeEventListener('variant:changed', this._variantChangedListener);
  }

  _init() {
    if (this._initialized) return;
    this._initialized = true;

    this._drawer = new Drawer(this, {
      onClose: () => {
        const container = this.querySelector('[data-oos-similar-products]');
        if (container) container.innerHTML = '';
      },
    });

    const notifyButton = this.querySelector('[data-oos-notify]');
    if (notifyButton) {
      notifyButton.addEventListener('click', () => {
        // Hidden BIS triggers do not track the current variant, so we use the visible one.
        const bisBtn = [...document.querySelectorAll('#BIS_trigger')].find((el) => el.offsetParent !== null);
        if (bisBtn) bisBtn.click();
        this._drawer.close();
      });
    }

    this._drawerRequested = false;
    this._recommendationsAvailable = true;

    this._clickListener = (e) => {
      const label = e.target.closest('.SizeSwatch--disabled');
      if (!label) return;

      const radio = document.getElementById(label.htmlFor);
      if (radio) {
        this._drawerRequested = true;
        this._loadTagalys(radio.value);
      }
    };
    document.addEventListener('click', this._clickListener);

    this._cartListener = async (e) => {
      const btn = e.target.closest('[data-oos-add-to-cart]');
      if (!btn || !this.contains(btn)) return;

      btn.disabled = true;
      const resp = await fetch(`${window.routes.cartAddUrl}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: parseInt(btn.dataset.oosAddToCart, 10), quantity: 1 }),
      });
      btn.disabled = false;

      if (resp.ok) {
        document.dispatchEvent(
          new CustomEvent('product:added', {
            bubbles: true,
            detail: { quantity: 1 },
          }),
        );
      }
    };
    document.addEventListener('click', this._cartListener);

    const triggerButton = document.querySelector('[data-oos-drawer-trigger]');
    if (triggerButton) {
      triggerButton.addEventListener('click', () => {
        const checkedOosRadio = document.querySelector('.SizeSwatch__Radio.disabled:checked');
        if (checkedOosRadio) {
          this._drawerRequested = true;
          this._loadTagalys(checkedOosRadio.value);
        }
      });
    }

    this._variantChangedListener = (e) => {
      const trigger = document.querySelector('[data-oos-drawer-trigger]');
      if (!trigger) return;
      const variant = e.detail?.variant;
      // Only hide here; showing is deferred to the Tagalys render callback
      if (!variant || variant.available) trigger.style.display = 'none';
    };
    document.addEventListener('variant:changed', this._variantChangedListener);
  }

  _loadTagalys(size) {
    const container = this.querySelector('[data-oos-similar-products]');
    if (!container || !size || !window.Tagalys?.UIWidgets) return;

    const sizeEl = this.querySelector('[data-oos-size]');
    if (sizeEl) sizeEl.textContent = size;

    const recommendationId = this.dataset.recommendationId || '';
    const productId = this.dataset.productId || '';
    const templateName = this.dataset.templateName || '';

    Tagalys.UIWidgets.Recommendations.init('[data-oos-similar-products]', {
      recommendationId,
      productId,
      callbacks: {
        beforeAPICall: (params) => Object.assign({}, params, { scope: { _variant_size: [size] } }),
        onSuccessfulAPICall: (response) => {
          setTimeout(() => {
            if (window.yotpoWidgetsContainer?.initWidgets) {
              window.yotpoWidgetsContainer.initWidgets();
            }
          }, 100);
          return response;
        },
      },
      templates: {
        widget: {
          options: { includeTagalysCSS: false, displayFormat: 'grid' },
          render: (html, args) => {
            if (args.props.loading === false) {
              const products = args.props.helpers.getProducts();
              if (!products.length) {
                this._recommendationsAvailable = false;
                this._drawerRequested = false;
                const trigger = document.querySelector('[data-oos-drawer-trigger]');
                if (trigger) trigger.style.display = 'none';
                return null;
              }
              this._recommendationsAvailable = true;
              const trigger = document.querySelector('[data-oos-drawer-trigger]');
              if (trigger) trigger.style.display = '';
              if (this._drawerRequested) {
                this._drawerRequested = false;
                this._drawer.open();
              }
              return html`<${args.templates.productList} />`;
            }
          },
        },
        product: {
          options: {
            productPageUrl: (product) => window.routes.rootUrlWithoutSlash + '/products/' + product.handle,
          },
          render: (html, args) => {
            const { product, helpers } = args.props;
            const variant = product.variants.find((v) => v.option1 === size && v.available);
            if (!variant) return null;

            const productLink = helpers.getTemplateOptions('product').productPageUrl(product);

            return html`
              <div class="ProductItem">
                <div class="image-container">
                  <a href="${productLink}" class="ProductItem__ImageWrapper">
                    <div class="AspectRatio AspectRatio--tall">
                      <img
                        class="ProductItem__Image Image--lazyLoad Image--fadeIn"
                        src="${product.featured_image.src}&width=300"
                        loading="lazy"
                        alt="${product.title}"
                      />
                    </div>
                  </a>
                  <button class="oos-add-to-cart" data-oos-add-to-cart="${variant.id}">
                    <span class="oos-add-to-cart__icon" aria-hidden="true">
                      <svg xmlns="http://www.w3.org/2000/svg" width="19" height="21" viewBox="0 0 19 21" fill="none">
                        <path d="M1 4.47559H17.575V20.0756H1V4.47559Z" fill="#FFFFFF" stroke="#252525"></path>
                        <path
                          d="M13.6754 4.47568C13.6754 2.32177 11.711 0.575684 9.28789 0.575684C6.86474 0.575684 4.90039 2.32177 4.90039 4.47568"
                          stroke="#252525"
                        ></path>
                      </svg>
                    </span>
                    <span class="oos-add-to-cart__plus" aria-hidden="true">
                      <svg xmlns="http://www.w3.org/2000/svg" width="10" height="10" viewBox="0 0 16 16" fill="none">
                        <g stroke="currentColor" fill="none" fill-rule="evenodd" stroke-linecap="square">
                          <path d="M8 1L8 15"></path>
                          <path d="M1 8L15 8"></path>
                        </g>
                      </svg>
                    </span>
                  </button>
                </div>
                <div class="ProductItem__Info">
                  <h3 class="ProductItem__Title Heading">
                    <a href="${productLink}">${product.title}</a>
                  </h3>
                  <div class="ProductItem__PriceList Heading">
                    ${product.compare_at_price > product.price
                      ? html`
                          <span class="ProductItem__Price Price Price--highlight Text--subdued"
                            >${helpers.formatCurrency(product.price)}</span
                          >
                          <span class="ProductItem__Price Price Price--compareAt Text--subdued"
                            >${helpers.formatCurrency(product.compare_at_price)}</span
                          >
                          <span class="discount_percentage_collection">
                            ${window.languages.productPricePercentOffJS.replace(
                              '#value#',
                              Math.round(((product.compare_at_price - product.price) / product.compare_at_price) * 100),
                            )}
                          </span>
                        `
                      : html`<span class="ProductItem__Price Price Text--subdued"
                          >${helpers.formatCurrency(product.price)}</span
                        >`}
                  </div>
                  <div class="yotpo-category-stars">
                    <div
                      class="yotpo-widget-instance"
                      data-yotpo-instance-id="729157"
                      data-yotpo-product-id="${product.id}"
                      data-yotpo-cart-product-id="${product.id}"
                      data-yotpo-section-id="${templateName}"
                    ></div>
                  </div>
                </div>
              </div>
            `;
          },
        },
      },
    });
  }
}
customElements.define('oos-variant-drawer', OosVariantDrawer);
